import { defineStore } from 'pinia';
import { computed, Ref, ref } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { BranchYearRoundDTO, IBranchYearRoundDTO, IPaginationDTO } from '@/dtos';
import {
  addBranchYearJudgesAPI,
  addBranchYearRoundAPI,
  getBranchYearJudgesAPI,
  getJudgingRoundsAPI,
  removeBranchYearJudgeAPI,
  updateBranchYearJudgeAPI,
} from '@/services/admin';
import { useUpdateStatusStore } from '../updateStatus';
import { BranchYearJudgeDTO, IBranchYearJudgeDTO } from '@/dtos/branch.year.judge.dto';

export const useAdminJudgeStore = defineStore('admin-judge', () => {
  const statusStore = useUpdateStatusStore();
  const queryClient = useQueryClient();
  const getBranchYearJudges = (
    branchId: Ref<string>,
    yearId: Ref<string>,
    pagination: Ref<IPaginationDTO>,
  ) =>
    useQuery({
      queryKey: computed(() => ['branch-year-judges', branchId.value, yearId.value]),
      queryFn: async (): Promise<{ data: IBranchYearJudgeDTO[]; count: number }> => {
        const ret = await getBranchYearJudgesAPI(branchId.value, yearId.value, pagination.value);
        const judges = await Promise.all<IBranchYearJudgeDTO[]>(
          ret.data.map((judge) => new BranchYearJudgeDTO(judge)),
        );
        return { data: judges, count: ret.count };
      },
      enabled: computed(() => !!branchId.value && !!yearId.value),
    });

  const getJudgingRounds = (yearId: Ref<string>, pagination: Ref<IPaginationDTO>) =>
    useQuery({
      queryKey: computed(() => ['branch-year-rounds', yearId.value]),
      queryFn: async (): Promise<{ data: IBranchYearRoundDTO[]; count: number }> => {
        const ret = await getJudgingRoundsAPI(yearId.value, pagination.value);
        const judges = await Promise.all<IBranchYearRoundDTO[]>(
          ret.data.map((judge) => new BranchYearRoundDTO(judge)),
        );
        return { data: judges, count: ret.count };
      },
      enabled: computed(() => !!yearId.value),
    });

  const add = useMutation({
    mutationFn: async ({ roundId, userIds }: { roundId: string; userIds: string[] }) => {
      return await addBranchYearJudgesAPI(roundId, userIds);
    },
    onSuccess: (data, variables) => {
      statusStore.setSuccessStatus(`branch-year-judges-${variables.roundId}`, 'success:update');
      queryClient.invalidateQueries({ queryKey: ['branch-year-judges'] });
    },
    onError: (err: string, variables) => {
      statusStore.setErrorStatus(`branch-year-judges-${variables.roundId}`, err);
    },
  });

  const remove = useMutation({
    mutationFn: async ({ judgeId, roundId }: { judgeId: string; roundId: string }) => {
      await removeBranchYearJudgeAPI(roundId, judgeId);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['branch-year-judges'] });
      statusStore.setSuccessStatus(`branch-year-judge-${variables.judgeId}`, 'success:delete');
    },
    onError: (err: string, variables) => {
      statusStore.setErrorStatus(`branch-year-judge-${variables.judgeId}`, err);
    },
  });

  const addRound = useMutation({
    mutationFn: async ({ branchId, yearId }: { branchId: string; yearId: string }) => {
      return await addBranchYearRoundAPI(branchId, yearId);
    },
    onSuccess: (data, variables) => {
      statusStore.setSuccessStatus(`branch-year-rounds-create}`, 'success:create');
      queryClient.invalidateQueries({ queryKey: ['branch-year-rounds'] });
    },
    onError: (err: string, variables) => {
      statusStore.setErrorStatus(`branch-year-rounds-create`, err);
    },
  });

  const updateBranchYearJudge = useMutation({
    mutationFn: (branchJudge: IBranchYearJudgeDTO) => {
      return updateBranchYearJudgeAPI(branchJudge);
    },
    onSuccess: (_data, branchJudge) => {
      queryClient.invalidateQueries({ queryKey: ['branch-year-judges'] });
      statusStore.setSuccessStatus(`branch-year-judge-update-${branchJudge.id}`, 'success:update');
    },
    onError: (err: string, branchJudge) => {
      statusStore.setErrorStatus(`branch-year-judge-update-${branchJudge.id}`, err);
    },
  });

  return {
    getBranchYearJudges,
    getJudgingRounds,
    add,
    remove,
    updateBranchYearJudge,
    addRound,
  };
});
